import { defineStore } from 'pinia';
// nextTick используется для того, чтобы выполнить операцию не сразу же, пока код ещё не отработал и vue не обновил DOM,
// а после обновления, когда в DOM-е выведутся актуальные данные
import { nextTick } from 'vue';

import { SaleOrderApiAdapter } from '../utils/saleOrderApiAdapter';

import { Tools } from '../utils/tools';
import { useEventBus } from './eventBus';

// Функционально эта сущность это не столько хранилище, сколько контроллер над остальными хранилищами. Но шаблоны с
// данными и методами этой сущности работают точно так же, как с обычными хранилищами, поэтому чтобы не плодить новые
// структуры и не усложнять реализацию/сопровождение шаблонов эта часть реализуется как хранилище.
export const orderStore = defineStore('order', {
	state: () => ({
		// загружен ли блок на текущий момент (не выполняются ли запросы на бэкенд или другие действия, его изменяющие)
		_isLoaded: false,
		_isInitialized: false,
		_isDisabled: false,
		_isDebugEnabled: false,
		_reloadInitiator: null,

		_orderDataPrototype: {
			currentLocationData: {},
			currentUserProfile: {},
			deliveryFields: {},
			deliverySystems: [],
			paymentSystems: [],
			personTypeDescriptions: {},
			personalFields: {},
			userDeliveryProfiles: [],
		},

		isReadyToOrder: false,

		personalFields: [],
		addressFields: [],
		allFields: [],
		locationField: null,
		/* здесь хранятся постоянные значения полей - объекты полей могут удаляться и добавляться (например, при смене 
		типа покупателя), но значения остаются здесь. В поле может быть невалидированное промежуточное значение, но 
		финальное, которое отправится на бекенд, хранится здесь. 
		*/
		fieldsPersistentValues: {},

		addressFieldsMap: {
			zip: null,
			location: null,
			address: null
		},
		addressDadataSuggestion: null,

		userProfiles: [],
		currentUserProfile: {},
		previousUserProfile: {},
		personTypes: [],
		currentPersonType: {},
		previousPersonType: {},
		deliverySystems: [],
		currentDeliverySystem: {},
		stores: [],
		currentStore: {},
		paymentSystems: [],
		currentPaymentSystem: {},

		userData: {},

		currentLocationData: {},

		selectedData: {
			personType: null,
			previousPersonType: null,
			userProfileId: null,
			previousUserProfileId: null,
			deliveryId: null,
			paymentId: null,
			storeId: null,
		},

		orderComment: null,
		userAgreement: false,
		
		eventBus: useEventBus()
	}),
	getters: {
		isLoaded() {
			return this._isLoaded;
		},
		
		isInitialized() {
			return this._isInitialized;
		},

		deliveryPrice() {
			return this.currentDeliverySystem?.price || 0;
		},
		
		isPickupDeliverySystemSelected() {
			return Object.keys(this.stores).length > 0;
		},
		
		getPropertyByCode: (store) => (code) => {
			for (const key in store.allFields) {
				if (store.allFields[key].code === code) {
					return store.allFields[key];
				}
			}
			
			return null;
		},
		
		fieldValue: (store) => (id) => {
			return store.fieldsPersistentValues[id] ?? null;
		},
		
		setFieldValue: (store) => (id, value) => {
			store.fieldsPersistentValues[id] = value;
		}
	},
	actions: {
		init() {
			this._reloadInitiator = 'init';
			this.eventBus.subscribe('o2k:basketStore:afterDataUpdate', this.handleBasketStorageUpdate.bind(this));
			this.eventBus.subscribe('o2k:order:externalOrderDataUpdate', this.externalOrderDataUpdate.bind(this));
			this.eventBus.subscribe('o2k:order:reloadOrderData', this.reloadOrderData.bind(this));
		},

		handleBasketStorageUpdate(event) {
			if (this._isDebugEnabled)
				console.log('handleBasketStorageUpdate initiator:' + event.data.initiator);

			if (event.data.initiator === 'order' || event.data.initiator === 'init')
				return;

			if (this._isDebugEnabled)
				console.log('handleBasketStorageUpdate run');

			this._reloadInitiator = 'basket';
			this.reloadOrderData();
		},

		externalOrderDataUpdate(event) {
			const initiator = event.data.initiator || 'unknown';
			if (this._isDebugEnabled)
				console.log('externalOrderDataUpdate initiator:' + initiator);

			this._reloadInitiator = initiator;
			if (event.data.orderData) {
				this.setOrderData(event.data.orderData);
			} else {
				this.reloadOrderData();
			}
		},

		_handleError(message, errorData = null, error = null) {
			if (error?.isNotice || !(error instanceof Error)) {
				this.eventBus.emit('o2k:order:notice', {message: message, errorObject: error, errorData: errorData});
			} else {
				this.eventBus.emit('o2k:order:error', {message: message, errorObject: error, errorData: errorData});
			}
		},

		disable() {
			this._isLoaded = true;
			this._isInitialized = true;
			this._isDisabled = true;
		},

		setOrderData(response) {
			try {
				if (this._isDebugEnabled) {
					console.log('order response received', response);
				}

				if (response.notifies?.[0]?.text) {
					this._handleError(response.notifies[0].text, response, null);
				}

				const initiator = this._reloadInitiator || 'order';
				this._reloadInitiator = null;

				const orderDataPrototype = Object.assign({}, this._orderDataPrototype);
				const orderData = Object.assign(orderDataPrototype, response);

				this.eventBus.emit(
					'o2k:orderStore:beforeDataUpdate',
					{initiator: initiator, data: response, orderData: orderData}
				);
			
				this.setCurrentLocationData(orderData);
				this.setDeliveryData(orderData);
				this.setUserData(orderData);
				this.setPersonalFieldsData(orderData);
				this.setAllFieldsData(orderData);
				this.setPersonTypesData(orderData);
				this.setAddressFieldsData(orderData);
				this.setUserProfilesData(orderData);
				this.setPaymentData(orderData);
				
				this._isLoaded = true;
				this._isInitialized = true;

				nextTick(() =>
					this.eventBus.emit(
						'o2k:orderStore:afterDataUpdate',
						{initiator: initiator, source: response, orderData: orderData}
					)
				);
			} catch (err) {
				console.error(err);
				const error = err.errors ? err.errors.slice(-1)[0] : err;
				this._handleError(error.message, null, error);
			}
		},

		reloadOrderData() {
			this._isLoaded = false;
			return SaleOrderApiAdapter.sendRefreshRequest(this)
				.then((response) => {
					this.setOrderData(response);
				})
				.catch(err => {
					this._handleError(err.message, null, err);
					this._isLoaded = true;
				});
		},

		changePersonType(personType) {
			if (this.currentPersonType?.id === personType?.id) return;

			this._isLoaded = false;
			this.previousPersonType = this.currentPersonType;
			this.currentPersonType = personType;

			this.reloadOrderData()?.then(() => {
				this.previousPersonType = this.currentPersonType;
			});
		},

		changeUserProfile(userProfile) {
			if (this.currentUserProfile?.id === userProfile?.id)
				return;

			this._isLoaded = false;
			this.previousUserProfile = this.currentUserProfile;
			this.currentUserProfile = userProfile;

			this.applyUserProfile();

			this.reloadOrderData()?.then(() => {
				this.previousUserProfile = this.currentUserProfile;
			});
		},

		changeUserAgreementStatus(userAgreementStatus) {
			if (this.userAgreement === userAgreementStatus)
				return;

			this.userAgreement = userAgreementStatus;
		},


		changeDebugStatus(debugStatus) {
			this._isDebugEnabled = debugStatus;
		},

		changeDelivery(delivery) {
			if (this.currentDeliverySystem?.id === delivery?.id)
				return;

			this.currentDeliverySystem = delivery;
			this.reloadOrderData();
		},

		changePayment(payment) {
			if (this.currentPaymentSystem?.id === payment?.id)
				return;

			this.currentPaymentSystem = payment;
			this.reloadOrderData();
		},

		changeStore(store) {
			if (this.currentStore?.id === store?.id)
				return;

			this.currentStore = store;
			this.reloadOrderData();
		},

		changeLocation(bxLocationCode) {
			const locationField = this.addressFieldsMap.location || {};
			if (this.fieldsPersistentValues[locationField.id] === bxLocationCode)
				return;

			this._isLoaded = false;

			const addressField = this.addressFieldsMap.address || {};
			addressField.value = '';
			this.fieldsPersistentValues[addressField.id] = '';
			addressField.selectedDadataSuggestion = null;

			this.fieldsPersistentValues[locationField.id] = bxLocationCode;
			this.reloadOrderData();
		},

		setDadataAddress(addressString, suggestion) {
			this.addressDadataSuggestion = suggestion;

			const dadataZipValue = suggestion?.data?.postal_code;
			if (this.addressFieldsMap.zip && dadataZipValue) {
				this.addressFieldsMap.zip.value = dadataZipValue;
				this.fieldsPersistentValues[this.addressFieldsMap.zip.id] = dadataZipValue;
			}
		},

		setUserData(orderData) {
			this._fillStoreObjectWithObject(
				'userData',
				orderData.currentUserProfile
			);
		},

		setPersonTypesData(orderData) {
			this._fillStoreObjectWithArray(
				'personTypes',
				'currentPersonType',
				Object.values(orderData.personTypeDescriptions)
			);
			
			this.previousPersonType = this.currentPersonType;
		},

		setUserProfilesData(orderData) {
			const locationField = this.addressFieldsMap.location || {};
			const location = this.fieldsPersistentValues[locationField.id];

			const profiles = orderData?.userDeliveryProfiles?.filter(
				(profile) => profile.fieldValues[locationField.id] === location
			) || [];

			this._fillStoreObjectWithArray(
				'userProfiles',
				'currentUserProfile',
				profiles,
				false
			);

			if (this.previousUserProfile !== this.currentUserProfile)
				this.applyUserProfile();

			this.previousUserProfile = this.currentUserProfile;
		},

		applyUserProfile() {
			const locationField = this.addressFieldsMap.location || {};
			const userProfile = this.currentUserProfile || {};

			if (this._isDebugEnabled)
				console.log('applyUserProfile', userProfile);

			this.addressFields.forEach((field) => {
				// для полей адреса переопределяем все значения кроме текущего города
				if (field.id === locationField.id) {
					return;
				}
				const fieldValue = userProfile?.fieldValues?.[field.id] || '';
				// также не переопределяем непустой индекс (zip) пустым значением - индекс рассчитывается автоматически бэком
				if (this.addressFieldsMap.zip?.id === field.id && this.fieldsPersistentValues[field.id] && !fieldValue)
					return;

				this.fieldsPersistentValues[field.id] = fieldValue;
			});
			this.personalFields.forEach((field) => {
				// для полей данных пользователя переопределяем только те поля, для которых значения нет
				const fieldVal = userProfile?.fieldValues?.[field.id];
				if (fieldVal && !this.fieldsPersistentValues[field.id])
					this.fieldsPersistentValues[field.id] = fieldVal;
			});
		},

		setPersonalFieldsData(orderData) {
			this._fillStoreFieldsArrayWithArray(
				'personalFields',
				Object.values(orderData.personalFields)
			);
		},

		setAllFieldsData(orderData) {
			this._fillStoreFieldsArrayWithArray(
				'allFields',
				Object.values(orderData.allFields)
			);
		},

		setCurrentLocationData(orderData) {
			this._fillStoreObjectWithObject(
				'currentLocationData',
				orderData.currentLocationData
			);
		},

		setDeliveryData(orderData) {
			this._fillStoreObjectWithArray(
				'deliverySystems',
				'currentDeliverySystem',
				orderData.deliverySystems,
				true
			);

			this._fillStoreObjectWithArray(
				'stores',
				'currentStore',
				Object.values(this.currentDeliverySystem?.store || {})
			);
		},

		setAddressFieldsData(orderData) {
			Object.values(orderData.deliveryFields).forEach((field) => {
				if (field.isZip === 'Y') {
					this.addressFieldsMap.zip = field;
				}
				if (field.isLocation === 'Y') {
					this.addressFieldsMap.location = field;
				}
				if (field.isAddress === 'Y') {
					this.addressFieldsMap.address = field;
				}
			});
			this._fillStoreFieldsArrayWithArray(
				'addressFields',
				Object.values(orderData.deliveryFields)
			);
		},

		setPaymentData(orderData) {
			this._fillStoreObjectWithArray(
				'paymentSystems',
				'currentPaymentSystem',
				orderData.paymentSystems
			);
		},

		_fillStoreObjectWithObject(storeObjectKey, data) {
			this[storeObjectKey] = data;
		},

		_fillStoreFieldsArrayWithArray(storeArrayKey, data) {
			this[storeArrayKey].splice(0);
			data.sort((a, b) => a.sort - b.sort);

			data.forEach((item) => {
				this[storeArrayKey].push(item);

				// если свойство заказа ранее не фиксировалось в объекте под хранение значений всех свойств, то
				// фиксируем его там
				if (!(item.id in this.fieldsPersistentValues)) {
					this.fieldsPersistentValues[item.id] = '';
				}
				
				const camelCaseFieldCode = Tools.snakeToCamel(item.code);
				
				if (!this.fieldsPersistentValues[item.id]) {
					if (item.value) {
						this.fieldsPersistentValues[item.id] = item.value;
					} else if (this.userData[camelCaseFieldCode]) {
						this.fieldsPersistentValues[item.id] = this.userData[camelCaseFieldCode];
					}
				}
				
				if (this.addressFieldsMap.location?.id === item.id) {
					this._applyCurrentLocation(this.fieldsPersistentValues[item.id]);
				}

				if (this.addressFieldsMap.zip?.id === item.id) {
					this._applyCurrentZip(this.fieldsPersistentValues[item.id]);
				}
			});
		},

		_applyCurrentLocation(locationCode) {
			if (window.BX.O2K?.OrderLocationSelectPopup) {
				window.BX.O2K.OrderLocationSelectPopup.currentLocationCode = locationCode;
			}
		},

		_applyCurrentZip() {
			// Implementation can be added here if needed
		},

		_fillStoreArrayWithArray(storeArrayKey, data) {
			this[storeArrayKey].splice(0);
			this[storeArrayKey].push(...data);
		},

		_fillStoreObjectWithArray(storeObjectKey, selectedDataKey, data, selectFirstIfNoneChecked = true) {
			this[storeObjectKey] = [];

			let isFound = false;
			data?.forEach((item) => {
				if (selectedDataKey !== null && item.checked) {
					isFound = true;
					this[selectedDataKey] = item;
				}
				this[storeObjectKey].push(item);
			});

			if (!isFound) {
				this[selectedDataKey] = null;
			}
			
			// если выбор первого доступного значения при отсутствии выбранных значений включен
			// и если выбранные значения сохраняются
			// и если нет выбранного значения (например, СД/оплаты)
			// и если есть хотя бы значение
			if (
				selectFirstIfNoneChecked
				&& selectedDataKey !== null
				&& !this[selectedDataKey]
				&& this[storeObjectKey].length > 0
			) {
				// то автоматически выбираем доступное значение
				this[selectedDataKey] = this[storeObjectKey][0];
			}
		},

		registerOrder() {
			this._isLoaded = false;
			let errorMessage = false;
			if (!this.currentPersonType) {
				errorMessage = 'Не выбран тип плательщика';
				return;
			}
			if (!this.currentDeliverySystem) {
				errorMessage = 'Не выбрана служба доставки';
				return;
			}
			if (!this.currentPaymentSystem) {
				errorMessage = 'Не выбрана служба оплаты';
				return;
			}

			if (errorMessage) {
				this._isLoaded = true;
				this.eventBus.emit('o2k:orderStore:orderRegisterError', { message: errorMessage });
				return;
			}

			this.eventBus.emitAsync('o2k:orderStore:beforeOrderRegister')
				.then(
					(results) => {
						try {
							const errorMessages = [];
							results.forEach((result) => {
								if (result && result.success === false) {
									errorMessages.push(result.message);
								}
							});
		
							if (errorMessages.length > 0) {
								this._isLoaded = true;
								this.eventBus.emit('o2k:orderStore:orderRegisterError', { message: errorMessages.join('<br><br>') });
							} else {
								SaleOrderApiAdapter.sendOrderRegisterRequest(this)
									.then(() => {
										this._isLoaded = true;
									})
									.catch(err => {
										this._handleError(err.message, null, err);
										this.eventBus.emit('o2k:orderStore:orderRegisterError', { message: err.message });
										this._isLoaded = true;
									});
							}
						} catch (err) {
							const error = err.errors ? err.errors.slice(-1)[0] : err;
							this._handleError(error.message, null, error);
						}
					});
		},
	},
});
